/**
 * stdb_socket_core — the SpacetimeDB socket's receiving half, shared by the socket worker and its in-page fallback
 * (the loading-screen reset, 2026-10-05). Pure: no DOM, no three; the WebSocket constructor and the outlet are passed in.
 *
 * WHY A WORKER. SpacetimeDB closes a socket it hears nothing from for 30 s (`DEFAULT_IDLE_TIMEOUT`, a Pong counts).
 * Chrome answers the server's pings in its network stack, but only while the page keeps READING the socket: a page
 * whose main thread is frozen stops draining it, the backlog holds the pings, the server times the client out
 * ("Client … timed out") and the reconnect law reloads the page. The owner's boot at high settings froze the page
 * 34–40 s (their beacon), and the probe at ultra froze it twice for 22 s back to back (verify/boot-stall-probe.mjs) —
 * the loading screen started over. A socket owned by a worker is drained on the worker's thread whatever the page is
 * doing, so a frozen frame no longer costs the connection.
 *
 * The SDK's own adapter (`WebsocketDecompressAdapter`) strips a one-byte compression tag and inflates gzip / brotli on
 * the main thread. This core does it off the page, and IN ARRIVAL ORDER: the SDK's adapter awaited each inflate
 * independently, so a small uncompressed message could be handed on before a large gzip one that arrived first.
 */

/** Page → core. */
export type CoreIn =
  | { type: 'open'; url: string; protocols: string[] }
  | { type: 'send'; data: ArrayBuffer }
  | { type: 'close' };

/** Core → page. `at` is the absolute time the frame arrived (ms since the epoch, `timeOrigin + now()`). */
export type CoreOut =
  | { type: 'open'; protocol: string }
  | { type: 'message'; buf: ArrayBuffer; off: number; len: number; at: number }
  | { type: 'error'; message: string; decode: boolean }
  | { type: 'close'; code: number; reason: string; wasClean: boolean };

/** The part of a WebSocket the core uses (a real one in the browser, a fake in the harness). */
export interface SocketLike {
  binaryType: string;
  readonly protocol: string;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onclose: ((ev: { code?: number; reason?: string; wasClean?: boolean }) => void) | null;
  send(data: ArrayBufferLike | ArrayBufferView): void;
  close(): void;
}
export type SocketCtor = new (url: string, protocols?: string | string[]) => SocketLike;
export type Inflate = (data: Uint8Array, format: 'gzip' | 'brotli') => Promise<Uint8Array>;
export type Outlet = (msg: CoreOut, transfer?: ArrayBuffer[]) => void;

/** The SDK's compression tags (`websocket_decompress_adapter.ts`): 0 none, 1 brotli, 2 gzip. */
export const TAG_NONE = 0;
export const TAG_BROTLI = 1;
export const TAG_GZIP = 2;

/** Strip the tag and inflate. Throws on a tag the SDK does not know. */
export function decodeFrame(bytes: Uint8Array, inflate: Inflate): Uint8Array | Promise<Uint8Array> {
  const tag = bytes[0];
  const data = bytes.subarray(1);
  if (tag === TAG_NONE) return data;
  if (tag === TAG_BROTLI) return inflate(data, 'brotli');
  if (tag === TAG_GZIP) return inflate(data, 'gzip');
  throw new Error(`Unexpected compression tag ${tag}`);
}

/** The SDK's own inflate (`sdk/decompress.ts`): a DecompressionStream fed in 128 KiB chunks, concatenated. */
export async function inflateStream(buffer: Uint8Array, format: 'gzip' | 'brotli', chunkSize = 128 * 1024): Promise<Uint8Array> {
  let offset = 0;
  const readable = new ReadableStream<Uint8Array>({
    pull(controller) {
      if (offset < buffer.length) {
        controller.enqueue(buffer.subarray(offset, Math.min(offset + chunkSize, buffer.length)));
        offset += chunkSize;
      } else controller.close();
    },
  });
  const reader = readable.pipeThrough(new DecompressionStream(format as CompressionFormat)).getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const r = await reader.read();
    if (r.done) break;
    chunks.push(r.value);
    total += r.value.length;
  }
  const out = new Uint8Array(total);
  let at = 0;
  for (const c of chunks) { out.set(c, at); at += c.length; }
  return out;
}

export class SocketCore {
  private ws: SocketLike | null = null;
  /** Frames still inflating; a later frame (or the close) waits behind them so order is kept. */
  private chain: Promise<void> = Promise.resolve();
  private pending = 0;

  constructor(
    private readonly Ctor: SocketCtor,
    private readonly out: Outlet,
    private readonly now: () => number,
    private readonly inflate: Inflate = inflateStream,
  ) {}

  handle(m: CoreIn): void {
    if (m.type === 'open') this.open(m.url, m.protocols);
    else if (m.type === 'send') { try { this.ws?.send(m.data); } catch (e) { this.out({ type: 'error', message: String(e), decode: false }); } }
    else if (m.type === 'close') { try { this.ws?.close(); } catch { /* already closing */ } }
  }

  private open(url: string, protocols: string[]): void {
    let ws: SocketLike;
    try {
      ws = new this.Ctor(url, protocols);
    } catch (e) {
      this.out({ type: 'error', message: String(e), decode: false });
      this.out({ type: 'close', code: 1006, reason: 'could not open', wasClean: false });
      return;
    }
    this.ws = ws;
    ws.binaryType = 'arraybuffer';
    ws.onopen = () => this.out({ type: 'open', protocol: ws.protocol });
    ws.onmessage = (ev) => this.onFrame(ev.data);
    ws.onerror = () => this.after(() => this.out({ type: 'error', message: 'websocket error', decode: false }));
    ws.onclose = (ev) => this.after(() => this.out({
      type: 'close', code: ev.code ?? 1006, reason: ev.reason ?? '', wasClean: ev.wasClean ?? false,
    }));
  }

  /** Run `fn` once every frame received before it has been handed on. */
  private after(fn: () => void): void {
    if (this.pending === 0) fn();
    else this.chain = this.chain.then(fn);
  }

  private onFrame(data: unknown): void {
    const at = this.now();
    const bytes = data instanceof ArrayBuffer ? new Uint8Array(data) : ArrayBuffer.isView(data)
      ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength) : null;
    if (!bytes || bytes.length === 0) return;
    const emit = (u: Uint8Array): void => {
      // The buffer is ours (the socket's frame or the inflate's output) — transfer it, no copy.
      this.out({ type: 'message', buf: u.buffer as ArrayBuffer, off: u.byteOffset, len: u.byteLength, at }, [u.buffer as ArrayBuffer]);
    };
    // Fast path: an uncompressed frame with nothing inflating ahead of it.
    if (this.pending === 0 && bytes[0] === TAG_NONE) { emit(bytes.subarray(1)); return; }
    this.pending++;
    this.chain = this.chain.then(async () => {
      try {
        emit(await decodeFrame(bytes, this.inflate));
      } catch (e) {
        // A frame we cannot read would leave the client's cache wrong; say so and drop the socket (the reconnect law).
        this.out({ type: 'error', message: `undecodable frame: ${String(e)}`, decode: true });
        try { this.ws?.close(); } catch { /* closing */ }
      } finally {
        this.pending--;
      }
    });
  }
}
